import React, { useState, useEffect, useRef, useMemo, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tasksApi, unavailabilitiesApi } from '../services/api';
import BottomNav from '../components/BottomNav';
import HelpGuide from '../components/HelpGuide';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  MapPin,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  CalendarDays,
  LayoutGrid,
  Calendar,
  CalendarOff,
  Plus,
  X,
  History,
  Camera,
  Eye,
  Navigation,
  Route,
  Play,
  MapPinned,
  Car,
  Map
} from 'lucide-react';
import { toast } from 'sonner';

// Lazy load RouteMap to avoid SSR issues with Leaflet
const RouteMap = lazy(() => import('../components/RouteMap'));
import { 
  format, 
  startOfWeek, 
  startOfMonth,
  endOfMonth,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  eachDayOfInterval,
  getDay,
  isBefore,
  isFuture,
  isAfter
} from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HousekeeperTasks = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [unavailabilities, setUnavailabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month', 'upcoming' or 'history'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showUnavailDialog, setShowUnavailDialog] = useState(false);
  const [unavailDate, setUnavailDate] = useState('');
  const [unavailReason, setUnavailReason] = useState('');
  const [unavailPeriod, setUnavailPeriod] = useState('full');
  const [savingUnavail, setSavingUnavail] = useState(false);
  
  // Route optimizer states
  const [showRouteDialog, setShowRouteDialog] = useState(false);
  const [startAddress, setStartAddress] = useState('');
  const [startAddressSuggestions, setStartAddressSuggestions] = useState([]);
  const [showStartSuggestions, setShowStartSuggestions] = useState(false);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [optimizedRoute, setOptimizedRoute] = useState(null);
  const [startCoords, setStartCoords] = useState(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, unavailRes] = await Promise.all([
        tasksApi.list(),
        unavailabilitiesApi.list()
      ]);
      setTasks(tasksRes.data);
      setUnavailabilities(unavailRes.data);
    } catch (error) {
      console.error('Tasks error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Week navigation
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Month navigation
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Pad start of month to align with week days
  const startDayOfWeek = getDay(monthStart);
  const paddingDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

  const navigatePrev = () => {
    if (viewMode === 'day') {
      setCurrentDate(subDays(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(subMonths(currentDate, 1));
    }
  };

  const navigateNext = () => {
    if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const goToToday = () => setCurrentDate(new Date());

  // Get tasks for upcoming weeks (next 4 weeks)
  const getUpcomingTasks = () => {
    const today = new Date();
    const fourWeeksLater = addWeeks(today, 4);
    return tasks
      .filter(t => {
        const taskDate = parseISO(t.date);
        return isAfter(taskDate, subDays(today, 1)) && isBefore(taskDate, fourWeeksLater);
      })
      .sort((a, b) => parseISO(a.date) - parseISO(b.date));
  };

  // Group upcoming tasks by week
  const getUpcomingTasksByWeek = () => {
    const upcomingTasks = getUpcomingTasks();
    const weeks = {};
    
    upcomingTasks.forEach(task => {
      const taskDate = parseISO(task.date);
      const weekStart = startOfWeek(taskDate, { weekStartsOn: 1 });
      const weekKey = format(weekStart, 'yyyy-MM-dd');
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          weekStart,
          weekLabel: `Semaine du ${format(weekStart, 'd MMMM', { locale: fr })}`,
          tasks: []
        };
      }
      weeks[weekKey].tasks.push(task);
    });
    
    return Object.values(weeks).sort((a, b) => a.weekStart - b.weekStart);
  };

  // Address autocomplete for route start point
  useEffect(() => {
    if (startAddress.length < 3) {
      setStartAddressSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(startAddress)}&limit=5`
        );
        const data = await response.json();
        if (data.features) {
          setStartAddressSuggestions(data.features.map(f => ({
            label: f.properties.label,
            lat: f.geometry.coordinates[1],
            lon: f.geometry.coordinates[0]
          })));
          setShowStartSuggestions(true);
        }
      } catch (error) {
        console.error('Address API error:', error);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [startAddress]);

  const selectStartAddress = (suggestion) => {
    setStartAddress(suggestion.label);
    setStartCoords({ lat: suggestion.lat, lon: suggestion.lon });
    setShowStartSuggestions(false);
  };

  // Get today's tasks for route optimization (memoized to avoid recalculating in JSX)
  const todaysTasks = useMemo(
    () => tasks.filter(t => isSameDay(parseISO(t.date), currentDate) && t.status !== 'termine'),
    [tasks, currentDate]
  );
  const getTodaysTasks = () => todaysTasks;

  // Memoize sorted/filtered task lists used heavily in JSX
  const currentMonthTasks = useMemo(
    () => tasks
      .filter(t => isSameMonth(parseISO(t.date), currentDate))
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
    [tasks, currentDate]
  );
  const completedTasksSorted = useMemo(
    () => tasks
      .filter(t => t.status === 'termine')
      .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [tasks]
  );
  const routeStops = useMemo(
    () => todaysTasks
      .map(t => ({ lat: t.property_lat, lon: t.property_lon, task: t }))
      .filter(s => s.lat && s.lon),
    [todaysTasks]
  );

  // Calculate optimized route using nearest neighbor algorithm
  const calculateOptimizedRoute = async () => {
    if (!startCoords) {
      toast.error('Veuillez sélectionner votre point de départ');
      return;
    }

    const todayTasks = getTodaysTasks();
    if (todayTasks.length === 0) {
      toast.error('Aucune prestation à optimiser aujourd\'hui');
      return;
    }

    setLoadingRoute(true);

    try {
      // Geocode task addresses
      const taskCoords = await Promise.all(
        todayTasks.map(async (task) => {
          const address = `${task.property_address}, ${task.property_name}`;
          try {
            const res = await fetch(
              `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(address)}&limit=1`
            );
            const data = await res.json();
            if (data.features && data.features.length > 0) {
              return {
                task,
                lat: data.features[0].geometry.coordinates[1],
                lon: data.features[0].geometry.coordinates[0]
              };
            }
          } catch (e) {
            console.error('Geocoding error:', e);
          }
          return null;
        })
      );

      const validCoords = taskCoords.filter(c => c !== null);

      if (validCoords.length === 0) {
        toast.error('Impossible de géolocaliser les adresses');
        setLoadingRoute(false);
        return;
      }

      // Nearest neighbor algorithm to optimize route
      const optimized = [];
      const remaining = [...validCoords];
      let currentPos = startCoords;

      while (remaining.length > 0) {
        let nearestIdx = 0;
        let nearestDist = Infinity;

        remaining.forEach((coord, idx) => {
          const dist = Math.sqrt(
            Math.pow(coord.lat - currentPos.lat, 2) + 
            Math.pow(coord.lon - currentPos.lon, 2)
          );
          if (dist < nearestDist) {
            nearestDist = dist;
            nearestIdx = idx;
          }
        });

        const nearest = remaining.splice(nearestIdx, 1)[0];
        optimized.push(nearest);
        currentPos = { lat: nearest.lat, lon: nearest.lon };
      }

      // Calculate distances using Haversine formula (no external API needed)
      const haversineDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Earth radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
      };

      // Calculate total distance
      let totalDistanceKm = 0;
      let prevCoord = startCoords;
      
      for (const stop of optimized) {
        totalDistanceKm += haversineDistance(prevCoord.lat, prevCoord.lon, stop.lat, stop.lon);
        prevCoord = { lat: stop.lat, lon: stop.lon };
      }

      // Estimate duration (average 40 km/h in urban areas + 5 min per stop)
      const estimatedDurationMin = Math.round((totalDistanceKm / 40) * 60 + (optimized.length * 5));

      setOptimizedRoute({
        stops: optimized,
        totalDistance: totalDistanceKm.toFixed(1),
        totalDuration: estimatedDurationMin
      });

      toast.success(`Itinéraire optimisé ! ${totalDistanceKm.toFixed(1)} km - ~${estimatedDurationMin} min`);
    } catch (error) {
      console.error('Route calculation error:', error);
      toast.error('Erreur lors du calcul de l\'itinéraire');
    } finally {
      setLoadingRoute(false);
    }
  };

  // Open route in Google Maps
  const openInGoogleMaps = () => {
    if (!optimizedRoute || !startCoords) return;

    const origin = `${startCoords.lat},${startCoords.lon}`;
    const waypoints = optimizedRoute.stops
      .slice(0, -1)
      .map(s => `${s.lat},${s.lon}`)
      .join('|');
    const destination = optimizedRoute.stops.length > 0 
      ? `${optimizedRoute.stops[optimizedRoute.stops.length - 1].lat},${optimizedRoute.stops[optimizedRoute.stops.length - 1].lon}`
      : origin;

    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints ? `&waypoints=${waypoints}` : ''}&travelmode=driving`;
    window.open(url, '_blank');
  };

  // Open route in Waze
  const openInWaze = () => {
    if (!optimizedRoute || optimizedRoute.stops.length === 0) return;
    const firstStop = optimizedRoute.stops[0];
    const url = `https://waze.com/ul?ll=${firstStop.lat},${firstStop.lon}&navigate=yes`;
    window.open(url, '_blank');
  };

  const getTasksForDay = (date) => {
    return tasks.filter(t => isSameDay(parseISO(t.date), date));
  };

  const isUnavailable = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return unavailabilities.find(u => u.date === dateStr);
  };

  // True only when the unavailability blocks the entire day
  const isFullDayOff = (date) => {
    const u = isUnavailable(date);
    if (!u) return false;
    return (u.period || 'full') === 'full';
  };

  const getPeriodLabel = (period) => {
    switch ((period || 'full').toLowerCase()) {
      case 'morning': return 'Matin';
      case 'afternoon': return 'Après-midi';
      default: return 'Journée complète';
    }
  };

  const handleAddUnavailability = async () => {
    if (!unavailDate) {
      toast.error('Veuillez sélectionner une date');
      return;
    }

    setSavingUnavail(true);
    try {
      await unavailabilitiesApi.create({
        date: unavailDate,
        reason: unavailReason || 'Congé',
        period: unavailPeriod || 'full'
      });
      toast.success('Indisponibilité enregistrée');
      setShowUnavailDialog(false);
      setUnavailDate('');
      setUnavailReason('');
      setUnavailPeriod('full');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingUnavail(false);
    }
  };

  const handleRemoveUnavailability = async (unavailId) => {
    try {
      await unavailabilitiesApi.delete(unavailId);
      toast.success('Indisponibilité supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Get status color
  const getStatusColor = (status, isUrgent) => {
    if (isUrgent) return 'bg-red-500';
    switch (status) {
      case 'affectee': return 'bg-amber-400';
      case 'acceptee': return 'bg-blue-400';
      case 'en_cours': return 'bg-purple-500';
      case 'termine': return 'bg-emerald-500';
      case 'probleme': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'affectee': return 'À accepter';
      case 'acceptee': return 'Acceptée';
      case 'en_cours': return 'En cours';
      case 'termine': return 'Terminé';
      case 'probleme': return 'Problème';
      default: return 'En attente';
    }
  };

  const getCardStyle = (status, isUrgent) => {
    if (isUrgent) return 'bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-l-red-500';
    switch (status) {
      case 'affectee': return 'bg-gradient-to-r from-amber-50 to-amber-100 border-l-4 border-l-amber-400';
      case 'acceptee': return 'bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-l-blue-400';
      case 'en_cours': return 'bg-gradient-to-r from-purple-50 to-purple-100 border-l-4 border-l-purple-500';
      case 'termine': return 'bg-gradient-to-r from-emerald-50 to-emerald-100 border-l-4 border-l-emerald-500';
      case 'probleme': return 'bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-l-red-500';
      default: return 'bg-gradient-to-r from-gray-50 to-gray-100 border-l-4 border-l-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center pb-safe">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <BottomNav />
      </div>
    );
  }

  // Get title based on view mode
  const getNavigationTitle = () => {
    if (viewMode === 'day') {
      return format(currentDate, "EEEE d MMMM yyyy", { locale: fr });
    } else if (viewMode === 'week') {
      return `${format(weekDays[0], 'd MMM', { locale: fr })} - ${format(weekDays[6], 'd MMM yyyy', { locale: fr })}`;
    } else {
      return format(currentDate, 'MMMM yyyy', { locale: fr });
    }
  };

  // Unavailability Card Component
  const UnavailabilityCard = ({ date, unavail }) => {
    const period = (unavail.period || 'full').toLowerCase();
    const isHalfDay = period === 'morning' || period === 'afternoon';
    return (
      <div className={`${isHalfDay ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'} border rounded-2xl p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${isHalfDay ? 'bg-orange-100' : 'bg-red-100'} rounded-full flex items-center justify-center`}>
              <CalendarOff className={`h-5 w-5 ${isHalfDay ? 'text-orange-600' : 'text-red-600'}`} />
            </div>
            <div>
              <p className={`font-medium ${isHalfDay ? 'text-orange-800' : 'text-red-800'}`}>
                {isHalfDay ? `OFF ${getPeriodLabel(period)}` : 'Jour OFF'}
              </p>
              <p className={`text-sm ${isHalfDay ? 'text-orange-600' : 'text-red-600'}`}>
                {unavail.reason || 'Indisponible'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className={`${isHalfDay ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-100' : 'text-red-600 hover:text-red-800 hover:bg-red-100'}`}
            onClick={() => handleRemoveUnavailability(unavail.id)}
            data-testid={`remove-unavail-${unavail.id}`}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-36">
      {/* Header */}
      <div className="bg-white pt-14 pb-4 px-4 border-b sticky top-0 z-20 shadow-sm">
        {/* Title */}
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Mon Planning</h1>
        
        {/* View Toggle - Full width row */}
        <div className="flex bg-slate-100 rounded-2xl p-1 mb-4">
          <button
            onClick={() => setViewMode('day')}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
              viewMode === 'day' 
                ? 'bg-white text-blue-900 shadow-sm' 
                : 'text-slate-500'
            }`}
            data-testid="view-day-btn"
          >
            <Calendar className="h-4 w-4" />
            Jour
          </button>
          <button
            onClick={() => setViewMode('week')}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
              viewMode === 'week' 
                ? 'bg-white text-blue-900 shadow-sm' 
                : 'text-slate-500'
            }`}
            data-testid="view-week-btn"
          >
            <CalendarDays className="h-4 w-4" />
            Semaine
          </button>
          <button
            onClick={() => setViewMode('upcoming')}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
              viewMode === 'upcoming' 
                ? 'bg-white text-purple-600 shadow-sm' 
                : 'text-slate-500'
            }`}
            data-testid="view-upcoming-btn"
          >
            <Eye className="h-4 w-4" />
            À venir
          </button>
          <button
            onClick={() => setViewMode('history')}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
              viewMode === 'history' 
                ? 'bg-white text-emerald-600 shadow-sm' 
                : 'text-slate-500'
            }`}
            data-testid="history-view-btn"
          >
            <History className="h-4 w-4" />
            Historique
          </button>
        </div>
        
        {/* Route optimizer button - Full width when visible */}
        {viewMode === 'day' && getTodaysTasks().length > 1 && (
          <Button
            className="w-full mb-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-2xl h-14 font-bold text-base shadow-lg shadow-emerald-500/30"
            onClick={() => setShowRouteDialog(true)}
            disabled={loadingRoute}
            data-testid="optimize-route-btn"
          >
            {loadingRoute ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <Route className="h-5 w-5 mr-2" />
            )}
            Optimiser ma route
          </Button>
        )}

        {/* Navigation - hide in history and upcoming views */}
        {viewMode !== 'history' && viewMode !== 'upcoming' && (
          <div className="flex items-center justify-between bg-slate-50 rounded-xl p-2">
            <Button variant="ghost" size="icon" onClick={navigatePrev} className="h-10 w-10 rounded-xl hover:bg-white">
              <ChevronLeft className="h-5 w-5 text-slate-600" />
            </Button>
            <p className="font-semibold text-slate-800 text-center">
              {getNavigationTitle()}
            </p>
            <Button variant="ghost" size="icon" onClick={navigateNext} className="h-10 w-10 rounded-xl hover:bg-white">
              <ChevronRight className="h-5 w-5 text-slate-600" />
            </Button>
          </div>
        )}
      </div>

      {/* Day View */}
      {viewMode === 'day' && (
        <div className="p-4">
          {/* Day Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center ${
                isFullDayOff(currentDate) ? 'bg-red-500 text-white' :
                isUnavailable(currentDate) ? 'bg-orange-500 text-white' :
                isToday(currentDate) ? 'bg-blue-600 text-white' : 'bg-white shadow-sm'
              }`}>
                <span className="text-2xl font-bold">{format(currentDate, 'd')}</span>
                <span className="text-[10px] uppercase">{format(currentDate, 'EEE', { locale: fr })}</span>
              </div>
              <div>
                <p className={`font-semibold capitalize ${
                  isFullDayOff(currentDate) ? 'text-red-600' :
                  isUnavailable(currentDate) ? 'text-orange-600' :
                  isToday(currentDate) ? 'text-blue-600' : 'text-gray-800'
                }`}>
                  {format(currentDate, 'EEEE', { locale: fr })}
                </p>
                <p className="text-sm text-gray-500">
                  {isFullDayOff(currentDate)
                    ? 'Jour OFF'
                    : isUnavailable(currentDate)
                      ? `OFF ${getPeriodLabel(isUnavailable(currentDate).period)} · ${getTasksForDay(currentDate).length} mission${getTasksForDay(currentDate).length > 1 ? 's' : ''}`
                      : `${getTasksForDay(currentDate).length} mission${getTasksForDay(currentDate).length > 1 ? 's' : ''}`}
                </p>
              </div>
            </div>
            
            {/* Add/Remove OFF button */}
            {!isBefore(currentDate, new Date().setHours(0,0,0,0)) && (
              isUnavailable(currentDate) ? (
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50 font-semibold px-4 py-2 rounded-xl"
                  onClick={() => handleRemoveUnavailability(isUnavailable(currentDate).id)}
                  data-testid="remove-off-btn"
                >
                  <X className="h-4 w-4 mr-2" />
                  Retirer OFF
                </Button>
              ) : (
                <Button
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-4 py-2 rounded-xl border-0"
                  onClick={() => {
                    setUnavailDate(format(currentDate, 'yyyy-MM-dd'));
                    setUnavailPeriod('full');
                    setShowUnavailDialog(true);
                  }}
                  data-testid="add-off-btn"
                >
                  <CalendarOff className="h-4 w-4 mr-2 text-slate-500" />
                  Me mettre OFF
                </Button>
              )
            )}
          </div>

          {/* Unavailability notice */}
          {isUnavailable(currentDate) && (
            <UnavailabilityCard date={currentDate} unavail={isUnavailable(currentDate)} />
          )}

          {/* Tasks for the day - shown unless full day OFF */}
          {!isFullDayOff(currentDate) && (
            getTasksForDay(currentDate).length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm mt-3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="h-8 w-8 text-gray-300" />
                </div>
                <p className="text-gray-500 font-medium">Aucune mission ce jour</p>
                <p className="text-gray-400 text-sm mt-1">
                  {isToday(currentDate) ? 'Profitez de votre journée !' : 'Pas de prestation prévue'}
                </p>
              </div>
            ) : (
              <div className={`space-y-3 ${isUnavailable(currentDate) ? 'mt-3' : ''}`}>
                {getTasksForDay(currentDate).map(task => (
                  <div
                    key={task.id}
                    onClick={() => navigate(`/app/task/${task.id}`)}
                    className={`rounded-2xl p-4 cursor-pointer transition-all hover:shadow-md ${getCardStyle(task.status, task.is_urgent)}`}
                    data-testid={`task-card-${task.id}`}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {task.status === 'termine' && (
                          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        )}
                        {task.is_urgent && task.status !== 'termine' && (
                          <AlertTriangle className="h-5 w-5 text-red-500" />
                        )}
                        <h3 className="font-bold text-gray-900">{task.property_name}</h3>
                      </div>
                      <span className="font-bold text-gray-800">{task.price || 0}€</span>
                    </div>

                    {/* Address */}
                    <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                      <MapPin className="h-3.5 w-3.5" />
                      <span className="truncate">{task.property_address}</span>
                    </div>

                    {/* Status & Time */}
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <Badge className={`border-0 bg-white/50 text-xs ${
                            task.status === 'termine' ? 'text-emerald-700' :
                            task.status === 'en_cours' ? 'text-purple-700' :
                            task.status === 'acceptee' ? 'text-blue-700' :
                            'text-amber-700'
                          }`}>
                            {getStatusLabel(task.status)}
                          </Badge>
                          {task.can_start_early && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                              Tôt OK
                            </span>
                          )}
                        </div>
                        {/* Teammate indicator */}
                        {task.housekeeper_names && task.housekeeper_names.length > 1 && (
                          <span className="text-xs text-purple-600 font-medium bg-purple-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                            En équipe avec {task.housekeeper_names.filter(n => n !== user?.name)[0]?.split(' ')[0]}
                          </span>
                        )}
                      </div>
                      {task.departure_time && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Départ: {task.departure_time}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Quick navigation to adjacent days with tasks */}
          <div className="mt-6 bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-xs font-semibold text-slate-500 mb-3 text-center">Navigation rapide</p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentDate(subDays(currentDate, 1))}
                className="flex-1 py-3 px-3 text-sm bg-slate-50 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors font-medium"
              >
                ← {format(subDays(currentDate, 1), 'd MMM', { locale: fr })}
              </button>
              <button
                onClick={goToToday}
                className={`flex-1 py-3 px-3 text-sm rounded-xl transition-colors font-semibold ${
                  isToday(currentDate) 
                    ? 'bg-blue-900 text-white' 
                    : 'bg-blue-100 text-blue-900 hover:bg-blue-200'
                }`}
              >
                Aujourd'hui
              </button>
              <button
                onClick={() => setCurrentDate(addDays(currentDate, 1))}
                className="flex-1 py-3 px-3 text-sm bg-slate-50 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors font-medium"
              >
                {format(addDays(currentDate, 1), 'd MMM', { locale: fr })} →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Week View - Column Layout */}
      {viewMode === 'week' && (
        <div className="p-2">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => {
              const unavail = isUnavailable(day);
              const fullOff = unavail && (unavail.period || 'full') === 'full';
              const halfOff = unavail && !fullOff;
              return (
                <div 
                  key={day.toISOString()}
                  onClick={() => { setCurrentDate(day); setViewMode('day'); }}
                  className={`text-center py-2 rounded-lg cursor-pointer transition-colors ${
                    fullOff ? 'bg-red-500 text-white' :
                    halfOff ? 'bg-orange-400 text-white' :
                    isToday(day) ? 'bg-blue-600 text-white' : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className={`text-[10px] font-medium uppercase ${
                    fullOff ? 'text-red-100' :
                    halfOff ? 'text-orange-50' :
                    isToday(day) ? 'text-blue-100' : 'text-gray-400'
                  }`}>
                    {fullOff ? 'OFF' :
                     halfOff ? (unavail.period === 'morning' ? 'AM-OFF' : 'PM-OFF') :
                     format(day, 'EEE', { locale: fr })}
                  </div>
                  <div className={`text-lg font-bold ${
                    fullOff || halfOff || isToday(day) ? 'text-white' : 'text-gray-800'
                  }`}>
                    {format(day, 'd')}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Task Columns */}
          <div className="grid grid-cols-7 gap-1" style={{ minHeight: '55vh' }}>
            {weekDays.map((day) => {
              const dayTasks = getTasksForDay(day);
              const unavail = isUnavailable(day);
              const fullOff = unavail && (unavail.period || 'full') === 'full';
              
              return (
                <div 
                  key={day.toISOString()}
                  className={`rounded-lg p-1 min-h-full ${
                    fullOff ? 'bg-red-100' :
                    unavail ? 'bg-orange-50' :
                    isToday(day) ? 'bg-white ring-2 ring-blue-500 ring-offset-1' : 'bg-white'
                  }`}
                >
                  {fullOff ? (
                    <div className="h-full flex flex-col items-center justify-center text-red-500">
                      <CalendarOff className="h-5 w-5 mb-1" />
                      <span className="text-[9px] font-medium">OFF</span>
                    </div>
                  ) : dayTasks.length === 0 ? (
                    unavail ? (
                      <div className="h-full flex flex-col items-center justify-center text-orange-500">
                        <CalendarOff className="h-4 w-4 mb-1" />
                        <span className="text-[8px] font-medium">
                          {unavail.period === 'morning' ? 'AM' : 'PM'} OFF
                        </span>
                      </div>
                    ) : (
                      <div className="h-full flex items-center justify-center">
                        <span className="text-gray-300 text-xs">-</span>
                      </div>
                    )
                  ) : (
                    <div className="space-y-1">
                      {unavail && (
                        <div className="bg-orange-100 text-orange-700 text-[8px] font-bold text-center py-0.5 rounded">
                          {unavail.period === 'morning' ? 'AM OFF' : 'PM OFF'}
                        </div>
                      )}
                      {dayTasks.map(task => (
                        <div
                          key={task.id}
                          onClick={() => navigate(`/app/task/${task.id}`)}
                          className={`p-1.5 rounded-lg cursor-pointer transition-all hover:opacity-80 ${getStatusColor(task.status, task.is_urgent)}`}
                          data-testid={`task-${task.id}`}
                        >
                          <div className="text-white text-[9px] font-bold leading-tight truncate">
                            {task.property_name?.split(' - ')[0] || 'Ménage'}
                          </div>
                          <div className="flex items-center gap-1 text-white/80 text-[8px] mt-0.5">
                            <span>{task.price || 0}€</span>
                            {task.can_start_early && (
                              <span className="bg-white/30 text-white px-1 rounded text-[7px] font-semibold">Tôt</span>
                            )}
                          </div>
                          {task.status === 'termine' && (
                            <CheckCircle2 className="h-3 w-3 text-white mt-0.5" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 bg-white rounded-xl p-3">
            <p className="text-xs font-medium text-gray-500 mb-2">Légende</p>
            <div className="flex flex-wrap gap-2">
              <span className="flex items-center gap-1 text-xs">
                <span className="w-3 h-3 rounded bg-red-500"></span> OFF
              </span>
              <span className="flex items-center gap-1 text-xs">
                <span className="w-3 h-3 rounded bg-amber-400"></span> À accepter
              </span>
              <span className="flex items-center gap-1 text-xs">
                <span className="w-3 h-3 rounded bg-blue-400"></span> Acceptée
              </span>
              <span className="flex items-center gap-1 text-xs">
                <span className="w-3 h-3 rounded bg-purple-500"></span> En cours
              </span>
              <span className="flex items-center gap-1 text-xs">
                <span className="w-3 h-3 rounded bg-emerald-500"></span> Terminé
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Month View - Calendar Grid */}
      {viewMode === 'month' && (
        <div className="p-3">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
              <div key={day} className="text-center py-2 text-xs font-medium text-gray-500">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Padding for start of month */}
            {Array.from({ length: paddingDays }).map((_, idx) => (
              <div key={`pad-${idx}`} className="aspect-square bg-gray-50 rounded-lg" />
            ))}
            
            {/* Month Days */}
            {monthDays.map((day) => {
              const dayTasks = getTasksForDay(day);
              const hasUnfinished = dayTasks.some(t => t.status !== 'termine');
              const unavail = isUnavailable(day);
              const fullOff = unavail && (unavail.period || 'full') === 'full';
              const halfOff = unavail && !fullOff;
              
              return (
                <div
                  key={day.toISOString()}
                  onClick={() => { setCurrentDate(day); setViewMode('day'); }}
                  className={`aspect-square rounded-lg p-1 relative cursor-pointer hover:opacity-80 transition-colors ${
                    fullOff ? 'bg-red-100' :
                    halfOff ? 'bg-orange-50' :
                    isToday(day) ? 'bg-white ring-2 ring-blue-500' : 'bg-white'
                  } ${!isSameMonth(day, currentDate) ? 'opacity-50' : ''}`}
                >
                  <div className={`text-xs font-medium mb-0.5 ${
                    fullOff ? 'text-red-600' :
                    halfOff ? 'text-orange-600' :
                    isToday(day) ? 'text-blue-600' : 'text-gray-700'
                  }`}>
                    {format(day, 'd')}
                  </div>
                  
                  {fullOff ? (
                    <div className="absolute inset-1 flex items-center justify-center">
                      <span className="text-[8px] font-bold text-red-500 bg-red-200 px-1 rounded">OFF</span>
                    </div>
                  ) : (
                    <>
                      {halfOff && (
                        <div className="absolute top-0.5 left-0.5">
                          <span className="text-[7px] font-bold text-orange-700 bg-orange-200 px-1 rounded">
                            {unavail.period === 'morning' ? 'AM' : 'PM'}
                          </span>
                        </div>
                      )}
                      {dayTasks.length > 0 && (
                        <div className="space-y-0.5 mt-1">
                          {dayTasks.slice(0, 2).map(task => (
                            <div
                              key={task.id}
                              className={`h-1.5 rounded-full ${getStatusColor(task.status, task.is_urgent)}`}
                              title={task.property_name}
                            />
                          ))}
                          {dayTasks.length > 2 && (
                            <div className="text-[8px] text-gray-500 text-center">
                              +{dayTasks.length - 2}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Notification dot for unfinished tasks */}
                  {hasUnfinished && dayTasks.length > 0 && !fullOff && (
                    <div className="absolute top-0.5 right-0.5 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Add OFF button for month view */}
          <Button
            className="w-full mt-4 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
            variant="outline"
            onClick={() => {
              setUnavailDate('');
              setShowUnavailDialog(true);
            }}
          >
            <CalendarOff className="h-4 w-4 mr-2" />
            Ajouter un jour OFF
          </Button>

          {/* Month Tasks List */}
          <div className="mt-4 space-y-3">
            <h3 className="font-semibold text-gray-800">
              Tâches de {format(currentDate, 'MMMM', { locale: fr })}
            </h3>
            
            {tasks.filter(t => isSameMonth(parseISO(t.date), currentDate)).length === 0 ? (
              <div className="bg-white rounded-xl p-6 text-center">
                <Clock className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Aucune mission ce mois</p>
              </div>
            ) : (
              <div className="space-y-2">
                {currentMonthTasks
                  .map(task => (
                    <div
                      key={task.id}
                      onClick={() => navigate(`/app/task/${task.id}`)}
                      className="bg-white rounded-xl p-3 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center text-white ${getStatusColor(task.status, task.is_urgent)}`}>
                        <span className="text-lg font-bold">{format(parseISO(task.date), 'd')}</span>
                        <span className="text-[9px] uppercase">{format(parseISO(task.date), 'EEE', { locale: fr })}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {task.status === 'termine' && (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                          )}
                          {task.is_urgent && task.status !== 'termine' && (
                            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                          )}
                          <h4 className="font-medium text-gray-900 truncate">{task.property_name}</h4>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className={`text-[10px] border-0 ${
                            task.status === 'termine' ? 'bg-emerald-100 text-emerald-700' :
                            task.status === 'en_cours' ? 'bg-purple-100 text-purple-700' :
                            task.status === 'acceptee' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {getStatusLabel(task.status)}
                          </Badge>
                          {task.departure_time && (
                            <span className="text-xs text-gray-500 flex items-center gap-0.5">
                              <Clock className="h-3 w-3" />
                              {task.departure_time}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="font-bold text-gray-800">{task.price || 0}€</span>
                    </div>
                  ))
                }
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="mt-4 bg-white rounded-xl p-3">
            <p className="text-xs font-medium text-gray-500 mb-2">Légende</p>
            <div className="flex flex-wrap gap-3">
              <span className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded-full bg-red-500"></span> OFF
              </span>
              <span className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded-full bg-amber-400"></span> À accepter
              </span>
              <span className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded-full bg-blue-400"></span> Acceptée
              </span>
              <span className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded-full bg-purple-500"></span> En cours
              </span>
              <span className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded-full bg-emerald-500"></span> Terminé
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming View - Next 4 weeks */}
      {viewMode === 'upcoming' && (
        <div className="p-4 pb-24">
          {/* Upcoming Header */}
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-4 mb-4 text-white">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <CalendarDays className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Prestations à venir</h2>
                <p className="text-purple-100 text-sm">
                  {getUpcomingTasks().length} prestation(s) dans les 4 prochaines semaines
                </p>
              </div>
            </div>
          </div>

          {/* Upcoming Tasks by Week */}
          {getUpcomingTasksByWeek().length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <CalendarDays className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Aucune prestation prévue</p>
              <p className="text-gray-400 text-sm">Vos prochaines prestations apparaîtront ici</p>
            </div>
          ) : (
            <div className="space-y-6">
              {getUpcomingTasksByWeek().map((week, weekIdx) => (
                <div key={weekIdx} className="space-y-2">
                  {/* Week Header */}
                  <div className="flex items-center gap-2 sticky top-0 bg-gray-50 py-2 z-10">
                    <div className="h-px flex-1 bg-purple-200"></div>
                    <span className="text-sm font-semibold text-purple-600 px-3 py-1 bg-purple-100 rounded-full">
                      {week.weekLabel}
                    </span>
                    <div className="h-px flex-1 bg-purple-200"></div>
                  </div>
                  
                  {/* Week Tasks */}
                  <div className="space-y-2">
                    {week.tasks.map(task => (
                      <div
                        key={task.id}
                        onClick={() => navigate(`/app/task/${task.id}`)}
                        className={`bg-white rounded-xl p-4 cursor-pointer transition-all hover:shadow-md border ${
                          isToday(parseISO(task.date)) ? 'border-purple-300 bg-purple-50' : 'border-gray-100'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Date Badge */}
                          <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${
                            isToday(parseISO(task.date)) 
                              ? 'bg-purple-500 text-white' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            <span className="text-lg font-bold">{format(parseISO(task.date), 'd')}</span>
                            <span className="text-[10px] uppercase">{format(parseISO(task.date), 'EEE', { locale: fr })}</span>
                          </div>
                          
                          {/* Task Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <h4 className="font-semibold text-gray-800 truncate">{task.property_name}</h4>
                                <p className="text-sm text-gray-500 flex items-center gap-1 truncate">
                                  <MapPin className="h-3 w-3 flex-shrink-0" />
                                  {task.property_address}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {task.can_start_early && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full whitespace-nowrap">Tôt OK</span>
                                )}
                                <Badge className={`${
                                  task.status === 'affectee' ? 'bg-amber-100 text-amber-700' :
                                  task.status === 'acceptee' ? 'bg-blue-100 text-blue-700' :
                                  task.status === 'en_cours' ? 'bg-purple-100 text-purple-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {task.status === 'affectee' ? 'À accepter' :
                                   task.status === 'acceptee' ? 'Acceptée' :
                                   task.status === 'en_cours' ? 'En cours' : task.status}
                                </Badge>
                              </div>
                            </div>
                            
                            {/* Teammate indicator */}
                            {task.housekeeper_names && task.housekeeper_names.length > 1 && (
                              <div className="mt-1">
                                <span className="text-xs text-purple-600 font-medium bg-purple-50 px-2 py-0.5 rounded inline-flex items-center gap-1">
                                  En équipe avec {task.housekeeper_names.filter(n => n !== user?.name)[0]?.split(' ')[0]}
                                </span>
                              </div>
                            )}
                            
                            {/* Task Options Indicators */}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              {task.guest_departure_time && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Départ {task.guest_departure_time}</span>
                              )}
                              {task.owner_arrival && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{task.owner_arrival}</span>
                              )}
                              <span className="text-xs text-emerald-600 font-semibold ml-auto">{task.price || 0}€</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="p-4 pb-24">
          {/* History Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-4 mb-4 text-white">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <History className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Historique des prestations</h2>
                <p className="text-emerald-100 text-sm">
                  {tasks.filter(t => t.status === 'termine').length} prestation(s) terminée(s)
                </p>
              </div>
            </div>
          </div>

          {/* Completed Tasks List */}
          {tasks.filter(t => t.status === 'termine').length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <History className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Aucune prestation terminée</p>
              <p className="text-gray-400 text-sm">Vos prestations terminées apparaîtront ici</p>
            </div>
          ) : (
            <div className="space-y-3">
              {completedTasksSorted
                .map(task => (
                  <div
                    key={task.id}
                    onClick={() => navigate(`/app/task/${task.id}`)}
                    className="bg-gradient-to-r from-emerald-50 to-white rounded-2xl p-4 cursor-pointer transition-all hover:shadow-md border border-emerald-100"
                    data-testid={`history-task-${task.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Date Badge */}
                      <div className="w-14 h-14 bg-emerald-500 rounded-xl flex flex-col items-center justify-center text-white flex-shrink-0">
                        <span className="text-lg font-bold">{format(parseISO(task.date), 'd')}</span>
                        <span className="text-[10px] uppercase">{format(parseISO(task.date), 'MMM', { locale: fr })}</span>
                      </div>
                      
                      {/* Task Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                          <h4 className="font-medium text-gray-900 truncate">{task.property_name}</h4>
                        </div>
                        <p className="text-sm text-gray-500 truncate mt-0.5">
                          <MapPin className="h-3 w-3 inline mr-1" />
                          {task.property_address || task.property_city || 'Adresse non renseignée'}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-0">
                            Terminé
                          </Badge>
                          {task.photos && task.photos.length > 0 && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Camera className="h-3 w-3" />
                              {task.photos.length} photo(s)
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Price & View */}
                      <div className="text-right flex flex-col items-end">
                        <span className="font-bold text-emerald-700 text-lg">{task.price || 0}€</span>
                        <span className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                          <Eye className="h-3 w-3" />
                          Voir détails
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          )}

          {/* Info Box */}
          <div className="mt-4 bg-blue-50 rounded-xl p-3 border border-blue-100">
            <p className="text-xs text-blue-700">
              💡 Cliquez sur une prestation pour voir les détails, les photos et vérifier les informations pour votre facture.
            </p>
          </div>
        </div>
      )}

      {/* Unavailability Dialog */}
      <Dialog open={showUnavailDialog} onOpenChange={(open) => {
        setShowUnavailDialog(open);
        if (!open) setUnavailPeriod('full');
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarOff className="h-5 w-5 text-red-500" />
              Ajouter un jour OFF
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={unavailDate}
                onChange={(e) => setUnavailDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                data-testid="unavail-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Période</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setUnavailPeriod('full')}
                  className={`py-3 px-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                    unavailPeriod === 'full'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid="period-full-btn"
                >
                  <div className="text-lg leading-none mb-1">●</div>
                  Journée
                </button>
                <button
                  type="button"
                  onClick={() => setUnavailPeriod('morning')}
                  className={`py-3 px-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                    unavailPeriod === 'morning'
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid="period-morning-btn"
                >
                  <div className="text-lg leading-none mb-1">◐</div>
                  Matin
                </button>
                <button
                  type="button"
                  onClick={() => setUnavailPeriod('afternoon')}
                  className={`py-3 px-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                    unavailPeriod === 'afternoon'
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid="period-afternoon-btn"
                >
                  <div className="text-lg leading-none mb-1">◑</div>
                  Après-midi
                </button>
              </div>
              {unavailPeriod !== 'full' && (
                <p className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-lg p-2">
                  💡 En demi-journée, vos missions du reste de la journée restent visibles dans votre planning.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Raison (optionnel)</Label>
              <Input
                placeholder="Ex: Congé, RDV médical..."
                value={unavailReason}
                onChange={(e) => setUnavailReason(e.target.value)}
                data-testid="unavail-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnavailDialog(false)}>
              Annuler
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleAddUnavailability}
              disabled={savingUnavail || !unavailDate}
              data-testid="confirm-unavail-btn"
            >
              {savingUnavail ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Route Optimizer Dialog */}
      <Dialog open={showRouteDialog} onOpenChange={setShowRouteDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[95vh] flex flex-col p-0 gap-0">
          <DialogHeader className="p-5 border-b bg-gradient-to-r from-emerald-500 to-teal-600">
            <DialogTitle className="flex items-center gap-2 text-white">
              <Route className="h-5 w-5" />
              Optimiser ma route du jour
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {/* Map Section */}
            <div className="p-4 bg-slate-50">
              <Suspense fallback={
                <div className="h-[250px] bg-slate-100 rounded-xl flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
                </div>
              }>
                <RouteMap 
                  startCoords={startCoords}
                  stops={routeStops}
                  optimizedRoute={optimizedRoute}
                  height="250px"
                />
              </Suspense>
            </div>

            <div className="p-5 space-y-4">
              {/* Start Address Input */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <MapPinned className="h-4 w-4 text-emerald-600" />
                  </div>
                  Mon point de départ
                </Label>
                <div className="relative">
                  <Input
                    value={startAddress}
                    onChange={(e) => setStartAddress(e.target.value)}
                    placeholder="Tapez votre adresse de départ..."
                    className="h-12 pl-4 pr-10 bg-slate-50 border-slate-200 rounded-xl focus:ring-emerald-500"
                  />
                  {startCoords && (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-500" />
                  )}
                  {showStartSuggestions && startAddressSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                      {startAddressSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.label || suggestion.id}
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-emerald-50 border-b last:border-0 text-sm flex items-center gap-2"
                          onClick={() => selectStartAddress(suggestion)}
                        >
                          <MapPin className="h-4 w-4 text-slate-400" />
                          {suggestion.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Today's Tasks */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Navigation className="h-4 w-4 text-blue-600" />
                  </div>
                  Mes prestations du jour
                  <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-bold rounded-full">
                    {getTodaysTasks().length}
                  </span>
                </Label>
                
                {getTodaysTasks().length === 0 ? (
                  <div className="p-6 bg-slate-50 rounded-xl text-center">
                    <p className="text-slate-500">Aucune prestation aujourd'hui</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {getTodaysTasks().map((task, idx) => (
                      <div key={task.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                        <span 
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-md"
                          style={{ backgroundColor: task.property_color || '#3B82F6' }}
                        >
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{task.property_name}</p>
                          <p className="text-xs text-slate-400 truncate">{task.property_address}</p>
                        </div>
                        <span className="text-sm font-semibold text-emerald-600">{task.price || 0}€</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Optimized Route Result */}
              {optimizedRoute && (
                <div className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
                      <CheckCircle2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-emerald-800">Itinéraire optimisé !</p>
                      <p className="text-xs text-emerald-600">{optimizedRoute.stops.length} étapes</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                      <p className="text-3xl font-bold text-emerald-600">{optimizedRoute.totalDistance}</p>
                      <p className="text-xs text-slate-500 mt-1">kilomètres</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                      <p className="text-3xl font-bold text-blue-600">{optimizedRoute.totalDuration}</p>
                      <p className="text-xs text-slate-500 mt-1">minutes</p>
                    </div>
                  </div>

                  {/* Optimized Order */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Ordre optimisé</p>
                    <div className="flex flex-wrap gap-2">
                      {optimizedRoute.stops.map((stop, idx) => (
                        <div 
                          key={stop.task?.id || `stop-${idx}`} 
                          className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg shadow-sm"
                        >
                          <span 
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                            style={{ backgroundColor: stop.task?.property_color || '#10B981' }}
                          >
                            {idx + 1}
                          </span>
                          <span className="text-sm font-medium text-slate-700 max-w-[100px] truncate">
                            {stop.task?.property_name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30"
                      onClick={openInGoogleMaps}
                    >
                      <Car className="h-5 w-5 mr-2" />
                      Google Maps
                    </Button>
                    <Button
                      className="flex-1 h-12 bg-cyan-500 hover:bg-cyan-600 shadow-lg shadow-cyan-500/30"
                      onClick={openInWaze}
                    >
                      <Navigation className="h-5 w-5 mr-2" />
                      Waze
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="p-5 border-t bg-slate-50 gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowRouteDialog(false)}
              className="h-12"
            >
              Fermer
            </Button>
            <Button
              className="flex-1 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg shadow-emerald-500/30"
              onClick={calculateOptimizedRoute}
              disabled={loadingRoute || !startCoords || getTodaysTasks().length === 0}
            >
              {loadingRoute ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Calcul en cours...
                </>
              ) : (
                <>
                  <Map className="h-5 w-5 mr-2" />
                  Calculer l'itinéraire
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HelpGuide />
      <BottomNav />
    </div>
  );
};

export default HousekeeperTasks;
